import { CommonModule, ViewportScroller } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, HostListener, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ChoosenPaymentType } from 'src/app/shared/components/choose-payment-method/choosen-payment-type.model';
import { PaymentMethodType } from 'src/app/shared/models/payment/payment-dto.model';
import { SharedModule } from 'src/app/shared/shared.module';
import { environment } from 'src/environments/environment';
import { FastPaymentsService } from '../_services/fast-payments.service';
import { ActivatedRoute, Router } from '@angular/router';
import { FastPaymentsTableComponent } from "../components/fast-payments-table/fast-payments-table.component";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, finalize, interval, Observable, of, Subject, Subscription, switchMap, takeUntil, timer } from 'rxjs';
import { FormInputComponent } from 'src/app/shared/components/forms/form-input/form-input.component';
import { FastPayments, FastPaymentStatus } from '../_model/fast-payments.model';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { FAST_PAYMENT_CONFIRMATION, FAST_PAYMENTS_ROUTE } from '../_consts/fast-payments-route.const';
import { TranslateService } from '@ngx-translate/core';
import { GhostButtonComponent } from 'src/app/shared/components/ghost-button/ghost-button.component';
import { LoadersComponent } from 'src/app/layout/loaders/loaders.component';
import { ChoosePaymentMethodComponent } from 'src/app/shared/components/choose-payment-method/choose-payment-method.component';

@Component({
  selector: 'app-fast-payments',
  standalone: true,
  imports: [
    CommonModule,
    FastPaymentsTableComponent,
    ReactiveFormsModule,
    GhostButtonComponent,
    LoadersComponent,
    FormInputComponent,
    ChoosePaymentMethodComponent
  ],
  templateUrl: './fast-payments.component.html',
  styleUrl: './fast-payments.component.scss'
})
export class FastPaymentsComponent implements OnInit, OnDestroy {
  @ViewChild('codeFirstInput') codeFirstInput: FormInputComponent;
  @ViewChild('codeSecondInput') codeSecondInput: FormInputComponent;
  content: {
    title: string;
    subtitle: string;
    provideCode: string;
    requiredError: string;
    patternError: string;
    acceptButton: string;
    processList: string[];
    howThisWork: string;
    processServices: string;
    cardAlt: string;
    driverLicence: string;
    vehicleAlt: string;
    vehicleRegistration: string;
    carAlt: string;
    dlExam: string;
    processPayment: string;
    youAreOnPage: string;
    thumbAlt: string;
  };
  splashEnabled: boolean = false;
  termsChecked: boolean = false;
  paymentStarted: boolean = false;
  fastPayment: FastPayments;
  env = environment;
  id: string;
  code: string;
  formGroup: FormGroup;
  destroyRef = inject(DestroyRef);
  waitingForData: boolean = false;
  PENDING_STATUSES = [FastPaymentStatus.AUTHPENDING];
  timeInterval: Subscription;
  private paymentPolling$: Subscription | null = null;
  private paymentSuccess$ = new Subject<void>();

  constructor(
    private service: FastPaymentsService,
    private router: Router,
    private toast: ToastrService,
    private translate: TranslateService,
    private vs: ViewportScroller
  ) {
    this.id = router.url.split('/')[2];

    this.formGroup = new FormGroup({
      codeFirst: new FormControl(null, [Validators.required, Validators.pattern(/^[ybndrfg8ejkmcpqxot1uwisza345h769]{3}$/i)]),
      codeSecond: new FormControl(null, [Validators.required, Validators.pattern(/^[ybndrfg8ejkmcpqxot1uwisza345h769]{3}$/i)]),
    });

    this.translate.get('FAST_PAYMENTS').subscribe({
      next: (content: any) => (this.content = content)
    });

    this.formGroup.get('codeFirst').statusChanges.subscribe(status => {
      if (status === 'VALID') {
        this.codeSecondInput.input.nativeElement.focus();
      }
    });

    this.formGroup.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: value => {
        if (value.codeFirst?.length > 3) {
          this.formGroup.get('codeFirst').patchValue(value.codeFirst.slice(0, 3));
          this.codeFirstInput.value = value.codeFirst.slice(0, 3);
          this.codeFirstInput.input.nativeElement.value = value.codeFirst.slice(0, 3);
          this.codeSecondInput.input.nativeElement.focus();
        }

        if (value.codeSecond?.length > 3) {
          this.formGroup.get('codeSecond').patchValue(value.codeSecond.slice(0, 3));;
          this.codeSecondInput.value = value.codeSecond.slice(0, 3);
          this.codeSecondInput.input.nativeElement.value = value.codeSecond.slice(0, 3);
        }

        if (value.codeFirst && value.codeSecond) {
          this.code = `${value.codeFirst}${value.codeSecond}`
        }
      }
    })
  }

  ngOnInit() {
    if (this.id) {
      this.getPayment(this.id).subscribe({
        next: (payment: FastPayments) => (this.fastPayment = payment),
        error: (error: { error: { detail: string } }) => {
          this.toast.error(error?.error?.detail);
        }
      });
    }
  }

  onPaste(data: string): void {
    const splitedData = data.slice(3, 6);

    this.formGroup.get('codeSecond').patchValue(splitedData);
  }

  getPayment(id: string): Observable<FastPayments> {
    this.waitingForData = true;
    return this.service.getPayment(id).pipe(finalize(() => this.waitingForData = false));
  }

  onCodeSubmit(): void {
    this.getPayment(this.code?.toLowerCase()).subscribe({
      next: (payment: FastPayments) => {
        this.fastPayment = payment;
        setTimeout(() => {
          this.vs.setOffset([0, 50])
          this.vs.scrollToAnchor('table')
        })
      },
      error: (err: any) => {
        this.toast.error(err?.error?.detail)
        console.log(err)
      }
    });
  }

  onFocus() {
    setTimeout(() => {
      navigator.clipboard.readText().then((text) => {
        this.onPaste(text)
      });
    }, 500); // Delay to allow paste action
  }

  onSubmit(event: ChoosenPaymentType) {
    switch (event.paymentType) {
      case PaymentMethodType.BLIK:
        this.proceedWithBlik(event.blikCode);
        break;
      default:
        console.error('Nieobsługiwany typ płatności');
        break;
    }
  }

  private proceedWithBlik(blikCode: string) {
    this.splashEnabled = true;
    this.paymentStarted = true;

    this.service.createBlikPayment$(this.fastPayment.paymentId?.id, blikCode, this.fastPayment.paymentSystem)
      .subscribe({
        next: (result) => {
          this.poolBlikPaymentStatus();
        },
        error: (error) => {
          this.handlePaymentError(null, error);
        }
      });
  }

  handlePaymentError(toastrMessage: string, error?: HttpErrorResponse) {
    this.splashEnabled = false;
    this.paymentStarted = false;
    toastrMessage ? this.toast.error(toastrMessage) : null;

    if (error.error?.errors?.length) {
      for (const e of error.error.errors) {
        e.userMessage ? this.toast.error(e.userMessage) : null;
        if (e.code === 'payment-already-started-or-accepted') {
          this.router.navigate(['/', e.path]);
        }
      }
    } else if (error?.error?.message?.length) {
      this.toast.error(error.error.message)
    } else if (error?.error?.detail) {
      this.toast.error(error?.error?.detail)
    }
  }

  private poolBlikPaymentStatus(): void {
    if (this.paymentPolling$) {
      return;
    }

    this.paymentPolling$ = timer(0, 3000)
      .pipe(
        switchMap(() => this.getPayment(this.id || this.code)),
        takeUntil(this.paymentSuccess$),
        finalize(() => {
          this.paymentPolling$?.unsubscribe();
          this.paymentPolling$ = null;
        })
      )
      .subscribe({
        next: (data: FastPayments) => this.handleBlikPaymentStatus(data),
        error: (error) => this.handlePaymentError(null, error)
      });
  }

  private handleBlikPaymentStatus(bill: FastPayments): boolean {
    if (bill.paymentStatus === FastPaymentStatus.EXCEEDED) {
      this.handlePaymentError('Płatnośc nie powiodła się');
    } else if (bill.paymentStatus === FastPaymentStatus.AUTHACCEPTED || bill.paymentStatus === FastPaymentStatus.AUTHREJECTED) {
      this.splashEnabled = false;
      this.paymentStarted = false;
      this.timeInterval ? this.timeInterval.unsubscribe() : null;
      this.router.navigate(['/', FAST_PAYMENTS_ROUTE, FAST_PAYMENT_CONFIRMATION, bill.paymentId?.id], {
        queryParams: {
          system: this.fastPayment.paymentSystem
        }
      });
    }
    return false;
  }

  onTermsChanged(value: boolean): void {
    this.termsChecked = value;
  }

  ngOnDestroy(): void {
    this.paymentPolling$.unsubscribe();
    this.paymentSuccess$.complete();
  }

  @HostListener('document:visibilitychange', ['$event'])
  visibilityAction(
    event: any
  ) {
    if (!document.hidden && this.paymentStarted) {
      this.poolBlikPaymentStatus();
    }
  }

  @HostListener('window:focus', ['$event'])
  focusAction(
    event: any
  ): void {
    if (this.paymentStarted) {
      this.poolBlikPaymentStatus();
    }
  }
}
